// ─────────────────────────────────────────────────────────────────────────────
// The tab bar steps aside while the reader scrolls down, and comes back on the way up.
//
// ⚠ THE DOCUMENT IS THE SCROLLER, not a div inside it. Chrome on Android and Safari on iOS fold
// their own toolbars away only when the PAGE scrolls; the app used to pin `html`/`body` at 100%
// with `overflow: hidden` and scroll each screen inside a `.pqm-scroll`, which is why the browser
// chrome stayed put here while it collapsed on other sites. So this listens on `window`.
//
// ⚠ The state lives on the ROOT as a class (`pqm-tabbar-hidden`). Every store update repaints the
// whole app and the tab bar is a new element each time; the root is the one node that survives.
// ─────────────────────────────────────────────────────────────────────────────

/** The root class that slides the tab bar out of view. */
export const TABBAR_HIDDEN_CLASS = "pqm-tabbar-hidden";

export interface ScrollChromeState {
    /** The offset the last decision was taken at. */
    y: number;
    hidden: boolean;
    /** Distance travelled in the current direction — positive down, negative up. */
    travel: number;
}

/** How far a scroll must carry in one direction before the bar reacts — a thumb's jitter must not. */
export const SCROLL_CHROME_THRESHOLD = 8;
/** Near the top there is nothing to make room for, and the bar never hides. */
export const SCROLL_CHROME_TOP_ZONE = 56;

/**
 * Decide whether the tab bar shows after the page scrolled to `y`.
 *
 * Shows at the top and at the very end — at the end because the last row would otherwise sit
 * under a bar that can only be brought back by scrolling UP past it. In between, it follows the
 * direction once the travel in that direction exceeds `threshold`; turning round starts the count
 * again, so a small wobble never flips it.
 *
 * @param maxY the largest offset the page can scroll to.
 */
export function nextScrollChrome(
    prev: ScrollChromeState,
    y: number,
    maxY: number,
    threshold: number = SCROLL_CHROME_THRESHOLD,
    topZone: number = SCROLL_CHROME_TOP_ZONE,
): ScrollChromeState {
    if (y <= topZone || y >= maxY - 2) return { y, hidden: false, travel: 0 };

    const delta = y - prev.y;
    if (delta === 0) return prev;

    const sameWay = delta > 0 === prev.travel > 0 && prev.travel !== 0;
    const travel = (sameWay ? prev.travel : 0) + delta;

    let hidden = prev.hidden;
    if (travel > threshold) hidden = true;
    else if (travel < -threshold) hidden = false;

    return { y, hidden, travel };
}

/**
 * Where the slim scroll indicator's thumb sits, in px from the top of the screen — or not visible,
 * when the page fits.
 *
 * The track runs from `trackTop` (below the sticky header) to `trackBottom`. The thumb is as tall
 * as the share of the page on screen (never under `minThumb`, or a long list leaves a speck) and
 * travels the track as the page scrolls.
 */
export function scrollIndicator(
    scrollY: number,
    viewport: number,
    scrollHeight: number,
    trackTop: number,
    trackBottom: number,
    minThumb = 28,
): { visible: boolean; top: number; height: number } {
    const track = trackBottom - trackTop;
    const maxY = scrollHeight - viewport;
    if (maxY <= 1 || track <= minThumb) return { visible: false, top: trackTop, height: 0 };

    const height = Math.max(minThumb, Math.round((viewport / scrollHeight) * track));
    const share = Math.min(1, Math.max(0, scrollY / maxY));

    return { visible: true, top: Math.round(trackTop + share * (track - height)), height };
}

export interface ScrollChrome {
    /** Bring the bar back and take the CURRENT offset as the baseline — for a new screen, or after a programmatic scroll. */
    reset(): void;
    /** Redraw the scroll indicator — after a repaint, which can change how long the page is. */
    refresh(): void;
}

/** The one scroll listener; module-level so a second init (the tests mount many times) replaces it. */
let scrollHandler: (() => void) | null = null;
let resizeHandler: (() => void) | null = null;

/**
 * The slim scroll indicator on the right edge — where in a long list the reader is (2026-10-18).
 *
 * ⚠ DRAWN BY THE APP, not the browser's scrollbar: on a phone that is an overlay that shows only
 * WHILE scrolling, and its styling is not reliably honoured. It is outside the app root, because
 * every repaint replaces the root's content, and it never takes a tap (`pointer-events: none`).
 */
function indicatorElement(): { track: HTMLElement; thumb: HTMLElement } {
    let track = document.querySelector<HTMLElement>("body > .pqm-scroll-indicator");
    if (!track) {
        track = document.createElement("div");
        track.className = "pqm-scroll-indicator";
        track.setAttribute("aria-hidden", "true");
        track.appendChild(document.createElement("div")).className = "pqm-scroll-thumb";
        document.body.appendChild(track);
    }
    return { track, thumb: track.firstElementChild as HTMLElement };
}

export function initScrollChrome(root: HTMLElement): ScrollChrome {
    if (scrollHandler) window.removeEventListener("scroll", scrollHandler);
    if (resizeHandler) window.removeEventListener("resize", resizeHandler);

    let state: ScrollChromeState = { y: window.scrollY, hidden: false, travel: 0 };
    let frame = 0;

    const apply = () => root.classList.toggle(TABBAR_HIDDEN_CLASS, state.hidden);

    const drawIndicator = () => {
        const { track, thumb } = indicatorElement();
        // Below the sticky header, above the bottom edge — the part of the screen that scrolls.
        const header = root.querySelector<HTMLElement>(".pqm-appbar");
        const top = (header ? header.getBoundingClientRect().bottom : 0) + 4;
        const bar = scrollIndicator(
            window.scrollY,
            window.innerHeight,
            document.documentElement.scrollHeight,
            top,
            window.innerHeight - 4,
        );
        track.hidden = !bar.visible;
        thumb.style.transform = `translateY(${bar.top}px)`;
        thumb.style.height = `${bar.height}px`;
    };

    const measure = () => {
        frame = 0;
        const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        state = nextScrollChrome(state, window.scrollY, maxY);
        apply();
        drawIndicator();
    };

    scrollHandler = () => {
        if (frame) return;
        frame = window.requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", scrollHandler, { passive: true });
    resizeHandler = drawIndicator;
    window.addEventListener("resize", resizeHandler);

    return {
        reset() {
            if (frame) {
                window.cancelAnimationFrame(frame);
                frame = 0;
            }
            state = { y: window.scrollY, hidden: false, travel: 0 };
            apply();
            drawIndicator();
        },
        refresh: drawIndicator,
    };
}
